import { http, HttpResponse, type HttpHandler } from "msw";
import { apiPaths } from "../../api-paths.js";
import type {
  Authentication_LoginByEmail_LoginByEmailCommandResponse_Success,
  Users_CheckUserExists_CheckUserExistsQueryResponse,
  Users_GetInfo_AuthenticationMethod,
  Users_GetInfo_GetInfoQueryResponse_Success,
  Users_RegisterByEmail_RegisterByEmailCommandResponse_Success,
} from "../../swagger-types/v1/index.js";

const loginByEmail: Authentication_LoginByEmail_LoginByEmailCommandResponse_Success = {
  accessToken: "FAKE_ACCESS_TOKEN_FOR_TEST_MOCKS_DO_NOT_USE",
  user: {
    id: 2000473,
    email: "mateusz.wrobel+test1@holisto.com",
    username: "mateusz.wrobel+test1@holisto.com",
    phone: null,
  },
};

const registerByEmail: Users_RegisterByEmail_RegisterByEmailCommandResponse_Success = {
  accessToken: "FAKE_ACCESS_TOKEN_FOR_TEST_MOCKS_DO_NOT_USE",
  user: {
    id: 2000473,
    email: "mateusz.wrobel+test1@holisto.com",
    username: "mateusz.wrobel+test1@holisto.com",
  },
};

const phoneAuthentication = {
  accessToken:
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIyMDAwMzkzIiwianRpIjoiNzZlNzNjOGUtY2IzMS00NWQ2LWFjYjItNTBlMjJkNzZkYWQ4IiwiYnJhbmRfaWQiOiI2IiwibmJmIjoxNzU5MzExMDU0LCJleHAiOjE3NTk5MTU4NTQsImlhdCI6MTc1OTMxMTA1NH0.I6hf1XA1GXASV2S7nGKg2GKKfRKg8I0GIJ03hSVc-wQ",
  user: {
    id: 2000393,
    email: "user@zaptravel.com",
    username: "user@zaptravel.com",
  },
};

const createDeleteUserHandler = (status: number, onRequest?: (request: Request) => void): HttpHandler[] => [
  http.delete(`**${apiPaths.users.delete}`, ({ request }) => {
    onRequest?.(request);
    return HttpResponse.json({}, { status });
  }),
];

const createSendAccountDeletionCodeHandler = (
  status: number,
  onRequest?: (request: Request) => Promise<void> | void,
): HttpHandler[] => [
  http.post(`**${apiPaths.users.sendAccountDeletionCode}`, async ({ request }) => {
    await onRequest?.(request);
    return HttpResponse.json({}, { status });
  }),
];

const createDeleteAccountV2Handler = (
  status: number,
  onRequest?: (request: Request) => Promise<void> | void,
): HttpHandler[] => [
  http.delete(`**${apiPaths.users.deleteV2}`, async ({ request }) => {
    await onRequest?.(request);
    return new HttpResponse(null, { status });
  }),
];

const createPendingHandler = (method: "post" | "delete", path: string): HttpHandler[] => [
  http[method](path, () => new Promise<Response>(() => {})),
];

const createUpdatePasswordHandler = (
  status: number,
  onRequest?: (request: Request) => Promise<void> | void,
): HttpHandler[] => [
  http.put(`**${apiPaths.users.updatePassword}`, async ({ request }) => {
    await onRequest?.(request);
    return HttpResponse.json({}, { status });
  }),
];

const createUpdateUserHandler = (
  status: number,
  onRequest?: (request: Request) => Promise<void> | void,
): HttpHandler[] => [
  http.patch(`**${apiPaths.users.update}`, async ({ request }) => {
    await onRequest?.(request);
    return HttpResponse.json({}, { status });
  }),
];

const createCompleteResetPasswordHandler = (
  status: number,
  onRequest?: (request: Request) => Promise<void> | void,
): HttpHandler[] => [
  http.post(`**${apiPaths.users.completeResetPassword}`, async ({ request }) => {
    await onRequest?.(request);
    return HttpResponse.json({}, { status });
  }),
];

const createCheckUserExistsHandler = (
  exists: boolean,
  onRequest?: (request: Request) => Promise<void> | void,
): HttpHandler[] => [
  http.get(`**${apiPaths.users.exists}`, async ({ request }) => {
    await onRequest?.(request);
    const email = new URL(request.url).searchParams.get("email");

    if (!email) {
      return HttpResponse.json({ message: "Email query parameter is required" }, { status: 400 });
    }

    const body: Users_CheckUserExists_CheckUserExistsQueryResponse = {
      exists,
    };

    return HttpResponse.json(body, { status: 200 });
  }),
];

const createRegisterByEmailHandler = (
  status: number,
  onRequest?: (request: Request) => Promise<void> | void,
): HttpHandler[] => [
  http.post(`**${apiPaths.users.registerByEmail}`, async ({ request }) => {
    await onRequest?.(request);
    return HttpResponse.json(registerByEmail, { status });
  }),
];

const createStartResetPasswordHandler = (
  status: number,
  onRequest?: (request: Request) => Promise<void> | void,
): HttpHandler[] => [
  http.post(`**${apiPaths.users.startResetPassword}`, async ({ request }) => {
    await onRequest?.(request);
    return HttpResponse.json({}, { status });
  }),
];

const createGetInfoHandler = (
  overrides?: {
    email?: string | null;
    authenticationMethods?: Users_GetInfo_AuthenticationMethod[];
  },
  onRequest?: (request: Request) => Promise<void> | void,
): HttpHandler[] => [
  http.get(`**${apiPaths.users.getInfo}`, async ({ request }) => {
    await onRequest?.(request);
    const body: Users_GetInfo_GetInfoQueryResponse_Success = {
      user: {
        email: overrides?.email !== undefined ? overrides.email : "pjeter.peszmek@holisto.com",
        id: 123,
        firstName: "Pjeter",
        lastName: "Peszmek",
        username: "pjeter.peszmek@holisto.com",
        agreeMarketing: false,
        countryCode: "pl",
        zipCode: "11-111",
        phone: {
          phone: "123123123",
          countryCode: "pl",
          callingCode: "48",
        },
      },
      authenticationMethods: overrides?.authenticationMethods ?? ["Username"],
    };
    return HttpResponse.json(body, { status: 200 });
  }),
];

export const userHandlersFactories = {
  getInfo: (
    overrides?: {
      email?: string | null;
      authenticationMethods?: Users_GetInfo_AuthenticationMethod[];
    },
    onRequest?: (request: Request) => Promise<void> | void,
  ) => createGetInfoHandler(overrides, onRequest),
  deleteUser: (onRequest?: (request: Request) => void) => createDeleteUserHandler(200, onRequest),
  deleteUserFailed: (onRequest?: (request: Request) => void) => createDeleteUserHandler(500, onRequest),
  sendAccountDeletionCode: (onRequest?: (request: Request) => Promise<void> | void) =>
    createSendAccountDeletionCodeHandler(200, onRequest),
  sendAccountDeletionCodeFailed: (onRequest?: (request: Request) => Promise<void> | void) =>
    createSendAccountDeletionCodeHandler(500, onRequest),
  sendAccountDeletionCodePending: () => createPendingHandler("post", `**${apiPaths.users.sendAccountDeletionCode}`),
  deleteAccountV2: (onRequest?: (request: Request) => Promise<void> | void) =>
    createDeleteAccountV2Handler(204, onRequest),
  deleteAccountV2Failed: (status: number, onRequest?: (request: Request) => Promise<void> | void) =>
    createDeleteAccountV2Handler(status, onRequest),
  deleteAccountV2Pending: () => createPendingHandler("delete", `**${apiPaths.users.deleteV2}`),
  updatePassword: (onRequest?: (request: Request) => Promise<void> | void) =>
    createUpdatePasswordHandler(200, onRequest),
  updatePasswordFailed: (onRequest?: (request: Request) => Promise<void> | void) =>
    createUpdatePasswordHandler(500, onRequest),
  updateUser: (onRequest?: (request: Request) => Promise<void> | void) => createUpdateUserHandler(200, onRequest),
  updateUserFailed: (onRequest?: (request: Request) => Promise<void> | void) => createUpdateUserHandler(500, onRequest),
  completeResetPassword: (onRequest?: (request: Request) => Promise<void> | void) =>
    createCompleteResetPasswordHandler(200, onRequest),
  completeResetPasswordFailed: (onRequest?: (request: Request) => Promise<void> | void) =>
    createCompleteResetPasswordHandler(500, onRequest),
  checkUserExists: ({
    exists = true,
    onRequest,
  }: {
    exists?: boolean;
    onRequest?: (request: Request) => Promise<void> | void;
  } = {}) => createCheckUserExistsHandler(exists, onRequest),
  registerByEmail: (onRequest?: (request: Request) => Promise<void> | void) =>
    createRegisterByEmailHandler(200, onRequest),
  registerByEmailFailed: (onRequest?: (request: Request) => Promise<void> | void) =>
    createRegisterByEmailHandler(500, onRequest),
  startResetPassword: (onRequest?: (request: Request) => Promise<void> | void) =>
    createStartResetPasswordHandler(200, onRequest),
  startResetPasswordFailed: (onRequest?: (request: Request) => Promise<void> | void) =>
    createStartResetPasswordHandler(500, onRequest),
} as const;

export const deleteUserHandlers = {
  success: userHandlersFactories.deleteUser(),
  failed: userHandlersFactories.deleteUserFailed(),
} as const;

export const completeResetPasswordSuccessHandler = http.post(`**${apiPaths.users.completeResetPassword}`, () => {
  return HttpResponse.json({}, { status: 200 });
});

export const userHandlersLegacy = {
  signUpUser: [
    http.post(`**${apiPaths.authentication.byEmail}`, () => {
      return HttpResponse.json(loginByEmail, { status: 200 });
    }),
  ],
  checkUserExists: [...createCheckUserExistsHandler(true)],
  signUpUserByPhoneAuthentication: [
    http.post(`*/v1/authentication/phone*`, () => {
      return HttpResponse.json(phoneAuthentication, { status: 200 });
    }),
  ],
};

export const deleteFacebookAccountHandlers = {
  "200Pending": [
    http.get(`**${apiPaths.users.facebookDeletionStatus}`, () => {
      return HttpResponse.json(
        {
          status: "Pending",
        },
        { status: 200 },
      );
    }),
  ],
  "200Completed": [
    http.get(`**${apiPaths.users.facebookDeletionStatus}`, () => {
      return HttpResponse.json(
        {
          status: "Completed",
        },
        { status: 200 },
      );
    }),
  ],
};

export const userHandlers = {
  getUser: [
    http.get(`**${apiPaths.users.getInfo}`, () => {
      const body: Users_GetInfo_GetInfoQueryResponse_Success = {
        user: {
          email: "pjeter.peszmek@holisto.com",
          id: 123,
          firstName: "Pjeter",
          lastName: "Peszmek",
          username: "pjeter.peszmek@holisto.com",
          agreeMarketing: false,
          countryCode: "pl",
          zipCode: "11-111",
          phone: {
            phone: "123123123",
            countryCode: "pl",
            callingCode: "48",
          },
        },
        authenticationMethods: ["Username"],
      };
      return HttpResponse.json(body, { status: 200 });
    }),
  ],
  completeResetPassword: [completeResetPasswordSuccessHandler],
};
